import React, { useState, useCallback } from 'react';
import { FormControl, Input, Flex, Text, Button, Box } from '@chakra-ui/core';
import { RiArrowUpCircleFill } from 'react-icons/ri';
import { AiOutlineCloseCircle } from 'react-icons/ai';

import { grayInputProps } from 'components/inputs/inputProps';
import { UserInfo } from 'model/user';
import { useDropzone } from 'react-dropzone';
import { greenButtonProps } from 'components/buttons/buttonProps';
import { awsConfig } from 'services/config/aws';
import styled from '@emotion/styled';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const S3 = require('react-s3');

const initialUserInfo: UserInfo = {
  email: '',
  firstName: '',
  lastName: '',
};

const csvHeader = 'Email,FirstName,LastName,FileName,UploadedAt';

const Home: React.FC = () => {
  const [userInfo, setUserInfo] = useState<UserInfo>(initialUserInfo);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);

  const onDrop = useCallback((acceptedFiles) => {
    setUploadFiles((prevUploadFiles: File[]) => {
      return prevUploadFiles.concat(
        acceptedFiles.map((file: any) =>
          Object.assign(file, { preview: URL.createObjectURL(file) })
        )
      );
    });
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { value, name } = e.currentTarget;
    setUserInfo({
      ...userInfo,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setUploading(true);
    const now = new Date();
    const time = now.toLocaleString().replace(/\//g, '.').replace(/,/g, '.');
    const today = now.toLocaleDateString().replace(/\//g, '.');
    for (const file of uploadFiles) {
      try {
        await S3.uploadFile(file, {
          ...awsConfig,
          dirName: `${userInfo.email}/${time}`,
        });

        const response = await fetch(
          `https://${awsConfig.bucketName}.s3.amazonaws.com/history/${today}.csv`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        const newCsvStr =
          '\n' +
          [
            userInfo.email,
            userInfo.firstName,
            userInfo.lastName,
            file.name,
            now.toLocaleString().replace(/,/g, '.'),
          ].join(',');

        if (response.status === 403) {
          const csvStr = csvHeader + newCsvStr;
          const blob = new Blob([csvStr], { type: 'text/plain' });
          const file = new File([blob], `${today}.csv`, { type: 'text/plain' });
          await S3.uploadFile(file, {
            ...awsConfig,
            dirName: `history`,
          });
        } else if (response.status === 200) {
          let bodyText = await response.text();
          console.log(bodyText);
          bodyText += newCsvStr;
          console.log(bodyText);
          const blob = new Blob([bodyText], { type: 'text/plain' });
          const file = new File([blob], `${today}.csv`, { type: 'text/plain' });
          console.log(file);
          await S3.uploadFile(file, {
            ...awsConfig,
            dirName: `history`,
          });
        }
      } catch {
        alert('Failed to upload files to the server.');
        setUploading(false);
        return;
      }
    }
    setUploadFiles([]);
    setUploading(false);
    setUserInfo(initialUserInfo);
    window.open('https://www.statementscanner.com/thank-you-for-uploading', '_blank');
  };

  const handleRemoveClick = (idx: number) => {
    setUploadFiles((prevUploadFiles) => {
      const files = [...prevUploadFiles];
      files.splice(idx, 1);
      return files;
    });
  };

  return (
    <Flex
      maxW="1400px"
      height="100%"
      margin="0 auto"
      flexDir="column"
      justifyContent="center"
      alignItems="center"
    >
      <FormControl minWidth="300px">
        <form onSubmit={handleSubmit}>
          <Text as="p" color="gray.600" fontSize="md">
            Info:
          </Text>
          <Input
            type="email"
            placeholder="Email"
            isRequired
            name="email"
            onChange={handleInputChange}
            {...grayInputProps}
          />
          <Input
            type="text"
            placeholder="First name"
            name="firstName"
            onChange={handleInputChange}
            isRequired
            {...grayInputProps}
          />
          <Input
            type="text"
            placeholder="Last name"
            name="lastName"
            isRequired
            onChange={handleInputChange}
            {...grayInputProps}
          />
          <Text as="p" color="gray.600" fontSize="md" marginTop="40px">
            Upload:
          </Text>
          <DropZoneWrapper disabled={uploading}>
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <Button
                variant="solid"
                borderRadius="5px"
                border="1px solid"
                borderColor="gray.200"
                color="gray.600"
                fontWeight="400"
                width="100%"
                margin="10px 0px"
                isDisabled={uploading}
              >
                <Box color="black" marginRight="5px">
                  <RiArrowUpCircleFill fontSize="30px" />
                </Box>
                Click to upload statements
              </Button>
            </div>
          </DropZoneWrapper>
          <Flex width="100%" marginTop="15px" flexDir="column">
            {uploadFiles.length > 0 &&
              uploadFiles.map((file: any, idx) => (
                <Flex key={idx} justifyContent="space-between">
                  <Text isTruncated>{file.path}</Text>
                  <Button
                    variant="link"
                    padding="0px"
                    width="30px"
                    minWidth="30px"
                    height="30px"
                    variantColor="gray"
                    borderRadius="50%"
                    onClick={() => handleRemoveClick(idx)}
                    isDisabled={uploading}
                  >
                    <AiOutlineCloseCircle fontSize="20px" />
                  </Button>
                </Flex>
              ))}
          </Flex>
          <Button {...greenButtonProps} type="submit" width="100%" isLoading={uploading}>
            Get Free CRM
          </Button>
        </form>
      </FormControl>
    </Flex>
  );
};

export default Home;

const DropZoneWrapper = styled.div<{ disabled: boolean }>`
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'initial')};
`;
